import { useEffect, useState } from "react";
import { motion } from "framer-motion";
import axios from "axios";
import { toast } from "sonner";
import { CalendarClock, Loader2, ArrowRight, CheckCircle2, Video, QrCode } from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const GOLD = "#D9A441";
const TEAL = "#5EC8BE";

const TIMEZONES = [
  "IST (India)",
  "GST (UAE / Dubai)",
  "GMT / BST (UK)",
  "CET (Europe)",
  "EST (US East)",
  "CST (US Central)",
  "PST (US West)",
  "SGT (Singapore)",
  "AEST (Australia East)",
  "JST (Japan)",
  "Other",
];

const today = () => new Date().toISOString().split("T")[0];
const blockKey = (date, slot) => `${date}|${slot}`;

export const SessionBooking = ({ prefill = {}, collectContact = false }) => {
  const [settings, setSettings] = useState(null);
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(null);
  const [form, setForm] = useState({
    first_name: prefill.first_name || "",
    email: prefill.email || "",
    whatsapp: prefill.whatsapp || "",
    date: "",
    time_slot: "",
    timezone: "IST (India)",
    note: "",
    txn_id: "",
    payment_datetime: "",
  });

  useEffect(() => {
    let active = true;
    axios
      .get(`${API}/session/settings`)
      .then((r) => {
        if (!active) return;
        setSettings(r.data);
        setForm((f) => ({ ...f, time_slot: r.data?.slots?.[0] || "" }));
      })
      .catch(() => {});
    return () => {
      active = false;
    };
  }, []);

  const update = (e) => {
    const { name, value } = e.target;
    setForm((f) => {
      const next = { ...f, [name]: value };
      // If the newly picked date makes the selected slot unavailable, move to the first free slot.
      if (name === "date" && settings) {
        const blocked = settings.blocked || [];
        if (blocked.includes(blockKey(value, next.time_slot))) {
          const free = (settings.slots || []).find((s) => !blocked.includes(blockKey(value, s)));
          next.time_slot = free || "";
        }
      }
      return next;
    });
  };

  const blocked = settings?.blocked || [];
  const isBlocked = (slot) => !!form.date && blocked.includes(blockKey(form.date, slot));

  const contactOk = collectContact ? form.first_name && form.email && form.whatsapp : true;
  const canSubmit = contactOk && form.date && form.time_slot && !isBlocked(form.time_slot) && !loading;

  const submit = async () => {
    setLoading(true);
    try {
      const { data } = await axios.post(`${API}/session/book`, form);
      setDone(data.reference);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not book your session. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  if (!settings || settings.active === false) return null;

  const inputCls =
    "w-full bg-transparent border-b border-white/15 py-2.5 text-sm placeholder:text-neutral-600 focus:border-white outline-none transition-colors duration-300";
  const priceLabel = `${settings.currency === "INR" ? "₹" : `${settings.currency} `}${settings.price}`;

  if (done) {
    return (
      <motion.div
        initial={{ opacity: 0, y: 12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.16, 1, 0.3, 1] }}
        data-testid="sb-success"
        className="mt-8 rounded-2xl border border-white/10 bg-black/30 p-6 text-center"
      >
        <span
          className="inline-flex h-12 w-12 items-center justify-center rounded-full mx-auto"
          style={{ background: "rgba(217,164,65,0.12)", color: GOLD }}
        >
          <CheckCircle2 size={24} />
        </span>
        <h4 className="text-lg font-bold tracking-tight mt-4">Session requested!</h4>
        <p className="text-sm text-neutral-400 mt-2 leading-relaxed">
          Your 1:1 request is in. If you&apos;ve paid, I&apos;ll match your transaction and confirm your slot over email or WhatsApp. Not paid yet? Scan the QR anytime and reply with your transaction ID.
        </p>
        <p className="text-xs text-neutral-600 mt-3">Reference: {done}</p>
      </motion.div>
    );
  }

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
      data-testid="session-booking"
      className="mt-8 text-left rounded-2xl border border-white/10 bg-gradient-to-b from-white/[0.04] to-transparent p-6"
    >
      <div className="flex items-start justify-between gap-3">
        <div className="flex items-center gap-2.5">
          <span
            className="inline-flex h-9 w-9 items-center justify-center rounded-full shrink-0"
            style={{ background: "rgba(217,164,65,0.12)", color: GOLD }}
          >
            <Video size={17} />
          </span>
          <div>
            <h4 className="text-base font-bold tracking-tight leading-tight">{settings.title}</h4>
            <p className="text-xs text-neutral-500 mt-0.5">{settings.duration_min} min · live &amp; personal</p>
          </div>
        </div>
        <span
          className="shrink-0 rounded-full px-3 py-1 text-sm font-semibold"
          style={{ background: "rgba(217,164,65,0.14)", color: GOLD }}
        >
          {priceLabel}
        </span>
      </div>

      <p className="text-xs text-neutral-500 mt-3 leading-relaxed flex items-start gap-1.5">
        <CalendarClock size={13} className="mt-0.5 shrink-0" style={{ color: TEAL }} />
        {settings.note}
      </p>

      <div className="mt-5 space-y-4">
        {collectContact && (
          <>
            <div>
              <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Your name *</label>
              <input data-testid="sb-name" name="first_name" value={form.first_name} onChange={update} className={inputCls} placeholder="Anubhav" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Email *</label>
                <input data-testid="sb-email" type="email" name="email" value={form.email} onChange={update} className={inputCls} placeholder="you@email.com" />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">WhatsApp *</label>
                <input data-testid="sb-whatsapp" name="whatsapp" value={form.whatsapp} onChange={update} className={inputCls} placeholder="+91 98765 43210" />
              </div>
            </div>
          </>
        )}

        <div className="grid grid-cols-2 gap-4">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Date *</label>
            <input
              data-testid="sb-date"
              type="date"
              name="date"
              min={today()}
              value={form.date}
              onChange={update}
              className={`${inputCls} [color-scheme:dark]`}
            />
          </div>
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Preferred time *</label>
            <select
              data-testid="sb-slot"
              name="time_slot"
              value={form.time_slot}
              onChange={update}
              className={`${inputCls} appearance-none`}
            >
              {(settings.slots || []).map((s) => {
                const b = isBlocked(s);
                return (
                  <option key={s} value={s} disabled={b} className="bg-[#0B0B0D]">
                    {s}{b ? " — Booked" : ""}
                  </option>
                );
              })}
            </select>
          </div>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Your timezone</label>
          <select
            data-testid="sb-timezone"
            name="timezone"
            value={form.timezone}
            onChange={update}
            className={`${inputCls} appearance-none`}
          >
            {TIMEZONES.map((tz) => (
              <option key={tz} value={tz} className="bg-[#0B0B0D]">
                {tz}
              </option>
            ))}
          </select>
        </div>

        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Anything specific? (optional)</label>
          <input
            data-testid="sb-note"
            name="note"
            value={form.note}
            onChange={update}
            className={inputCls}
            placeholder="What you'd like to cover…"
          />
        </div>
      </div>

      {/* Payment via QR */}
      {settings.qr_image_url && (
        <div className="mt-6 rounded-2xl border border-white/10 bg-black/30 p-5">
          <div className="flex items-center gap-2 mb-3">
            <QrCode size={16} style={{ color: GOLD }} />
            <p className="text-sm font-semibold">Pay {priceLabel} to confirm faster</p>
          </div>
          <div className="flex flex-col sm:flex-row gap-5 items-center sm:items-start">
            <div className="bg-white rounded-xl p-2 shrink-0">
              <img
                data-testid="sb-qr"
                src={settings.qr_image_url}
                alt="Scan to pay"
                className="h-40 w-40 object-contain"
              />
            </div>
            <div className="flex-1 w-full">
              <p className="text-xs text-neutral-500 leading-relaxed mb-3">
                Scan the QR with any UPI app to pay {priceLabel}. Then add your transaction details below — or pay later and reply to our email/WhatsApp with the ID.
              </p>
              <div className="space-y-3">
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Transaction / Reference ID</label>
                  <input data-testid="sb-txn" name="txn_id" value={form.txn_id} onChange={update} className={inputCls} placeholder="e.g. 4318XXXXXXXX" />
                </div>
                <div>
                  <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Date &amp; time of payment</label>
                  <input data-testid="sb-paidat" type="datetime-local" name="payment_datetime" value={form.payment_datetime} onChange={update} className={`${inputCls} [color-scheme:dark]`} />
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      <button
        data-testid="sb-submit"
        disabled={!canSubmit}
        onClick={submit}
        className="mt-6 w-full flex items-center justify-center gap-2 rounded-full text-black text-sm font-semibold py-3.5 transition-transform duration-300 hover:scale-[1.01] disabled:opacity-40 disabled:cursor-not-allowed"
        style={{ background: GOLD }}
      >
        {loading ? (
          <>
            <Loader2 size={16} className="animate-spin" /> Booking…
          </>
        ) : (
          <>
            Book my session <ArrowRight size={16} />
          </>
        )}
      </button>
    </motion.div>
  );
};

export default SessionBooking;
